import DesignTokens from '@tableau/design-tokens';
import { Style } from '@tableau/vizportalclient-common-components';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { cssClass, fillStyle, flexColumnStyle } from '../../style';

const cssPrefix = 'SpaceContainer';

export const TabpanelScrollContainerTestIdSuffix = 'tabpanel-scroll-container';

export namespace SpaceContainerStyles {

  export const NoStyleHeader: Style = {
    display: 'inline',
    fontWeight: 'inherit',
    fontSize: 'inherit',
    margin: 0,
    padding: 0
  };

  export const SpaceContainer = cssClass(cssPrefix, 'space-container', fillStyle, flexColumnStyle, {
    // For spaces with tabs and subplaces, the tabs widget renders a scroll container with some padding,
    // which we don't use because we display the subplace contents separately. So, find that div
    // using the only identifier we have, and hide it completely.
    '$nest': {
      [`div[data-tb-test-id$="${TabpanelScrollContainerTestIdSuffix}"]`]: {
        display: 'none',
      }
    }
  });

  export const GlobalSpaceHorizontalPadding = TabStyles.Spacing.ExtraLargeSpace;

  export const MastheadArea = cssClass(cssPrefix, 'space-masthead-area', {
    paddingTop: TabStyles.Spacing.LargeSpace,
    paddingRight: GlobalSpaceHorizontalPadding,
    paddingBottom: TabStyles.Spacing.MediumSpace,
    paddingLeft: GlobalSpaceHorizontalPadding,
  });

  export const HeaderClassName = 'header';
  export const ExplorerToolbarClassName = 'explorer-toolbar';

  export const WithExplorerToolbarInContentSpace = cssClass(cssPrefix, 'with-explorer-toolbar-in-content-space', {
    paddingBottom: 0
  });

  export const WithExplorerToolbar = cssClass(cssPrefix, 'with-explorer-toolbar', {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'end',
    paddingBottom: 0,
    $nest: {
      ['.' + HeaderClassName]: {
        marginRight: CommonStyles.Sizes.XLargeSpace
      },
      ['.' + ExplorerToolbarClassName]: {
        flex: '1 1 auto'
      }
    }
  });

  export const MastheadSubheadingArea: React.CSSProperties = {
    minWidth: 0, // 0 is the default
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102),
    fontSize: '14px',
  };

  export const SubplaceArea = cssClass(cssPrefix, 'space-subplace-area', {
    flex: 1,
    position: 'relative'
  });

  export const SubplaceAreaCenterMargins = cssClass(cssPrefix, 'space-subplace-area-center-margins', {
    margin: '0 24px 0'
  });

  export const SubplaceContainer = cssClass(cssPrefix, 'space-subplace-container', fillStyle, {
    // For subspace components that use flexbox layout at their root to fill the entire area,
    // the container needs to be a flex element. One might think it needs to have flex-direction: column,
    // but this appears not to be the case.
    display: 'flex',
  });

  export const SpaceTabsContainer = cssClass(cssPrefix, 'space-tabs-container', {
    marginTop: CommonStyles.Sizes.DefaultSpace,
    $nest: {
      ['div[role="tablist"]']: {
        backgroundColor: 'transparent !important' as any
      }
    }
  });

  export const SpaceTabHeight = 26;

  export const SpaceTabsBottom = cssClass(cssPrefix, 'space-tabs-bottom', {
    height: 3, // Matches the height of the bottom bar of the TabsWidget
    backgroundColor: applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3),
    width: '100%',
    position: 'relative',
    top: SpaceTabHeight,
    marginBottom: '1px' // To allow for the focus indicator
  });

  export const SpaceTabContainer = cssClass(cssPrefix, 'space-tab-container', {
    fontSize: '15px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flexDirection: 'row',
    paddingBottom: CommonStyles.Sizes.DefaultSpace,
    height: SpaceTabHeight,
    boxSizing: 'border-box'
  });

  export const SpaceTabHeader = cssClass(cssPrefix, 'space-tab-header', NoStyleHeader);

  export const SpaceTabCountContainer = cssClass(cssPrefix, 'space-tab-count-container', {
    marginLeft: TabStyles.Spacing.MediumSpace,
    fontSize: '12px',
    marginBottom: '-1px' // count vertically align with the tab label
  });

  export const SpaceCenteredContent = cssClass(cssPrefix, 'space-centered-content', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  });

  export const SpaceTabLowEmphesis = cssClass(cssPrefix, 'space-tab-low-emphesis', {
    color: applyColor(DesignTokens.color.font.default.subtle, TabStyles.Colors.F7),
    $nest: {
      '&:hover': {
        color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
      }
    }
  });
}
